import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Briefcase } from 'lucide-react'
import { ApiError, request } from '@waago/api'
import { Button, Checkbox, ServiceIcon, SERVICE_LABEL, type ServiceActivity } from '@waago/ui'

/**
 * "Devenir prestataire" — the screen an account sees when it signs in to Waago
 * Pro without being a provider yet.
 *
 * <p>One platform, one identity: Waago Pro signs in with the same phone number
 * and password as the client app, so somebody who already had a client account
 * never meets the signup wizard — they log in. Until this screen existed they
 * landed on a provider dashboard belonging to an account that is not a
 * provider: "Indisponible", 0 FCFA, 0 missions, and every save failing at the
 * last step with an internal error. Reported in UAT on 2026-10-12, where the
 * tester put it plainly: "i signed in with tel + password, no need a sms code".
 *
 * <p>No code is sent here, and none is needed. The session was issued against
 * that number, which is exactly what the SMS in signup is spent proving — and
 * asking for a second proof of the same thing is how a provider gives up.
 */
const ACTIVITIES: ServiceActivity[] = [
  'TRANSPORT_PERSON',
  'DELIVERY',
  'EQUIPMENT_RENTAL',
  'DRIVER_HIRE',
]

export function BecomeProvider({ name }: { name: string | null }) {
  const queryClient = useQueryClient()
  const [activities, setActivities] = useState<ServiceActivity[]>([])
  const [error, setError] = useState<string | null>(null)

  const start = useMutation({
    mutationFn: () => request('/me/provider', { method: 'POST', body: { activities } }),
    onSuccess: () => {
      // Everything on this account changes at once — the dashboard, the
      // services it now has as drafts, the availability switch.
      void queryClient.invalidateQueries()
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Le compte prestataire n’a pas pu être ouvert.'),
  })

  return (
    <div className="flex flex-col gap-6 px-6 py-10">
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="bg-brand-50 grid size-16 place-items-center rounded-full">
          <Briefcase className="text-brand-700 size-7" aria-hidden="true" />
        </span>
        <h1 className="text-lg font-bold text-slate-900">
          {name ? `Bienvenue ${name},` : 'Bienvenue,'} ouvrez votre compte prestataire
        </h1>
        <p className="max-w-xs text-sm leading-5 text-slate-600">
          Ce numéro a déjà un compte WaaGo. Choisissez vos activités pour commencer à recevoir des
          missions — vous gardez le même compte et le même mot de passe.
        </p>
      </div>

      {error ? (
        <p
          role="alert"
          className="animate-slide-up rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          {error}
        </p>
      ) : null}

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-sm font-medium text-slate-900">Vos activités</legend>
        {ACTIVITIES.map((activity) => {
          const on = activities.includes(activity)
          return (
            <Checkbox
              key={activity}
              label={
                <span className="flex items-center gap-3">
                  <ServiceIcon activity={activity} className="text-brand-600 size-5" />
                  {SERVICE_LABEL[activity]}
                </span>
              }
              checked={on}
              onChange={() =>
                setActivities(
                  on ? activities.filter((a) => a !== activity) : [...activities, activity],
                )
              }
              className="rounded-md border border-slate-200 px-3 py-3"
            />
          )
        })}
      </fieldset>

      <Button
        block
        disabled={activities.length === 0 || start.isPending}
        onClick={() => {
          setError(null)
          start.mutate()
        }}
      >
        {start.isPending ? 'Ouverture…' : 'Devenir prestataire'}
      </Button>

      <p className="text-2xs text-center leading-4 text-slate-500">
        Un service brouillon est créé pour chaque activité. Vous fixez vos tarifs avant de les
        publier.
      </p>
    </div>
  )
}
